import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Download, Palette, Star, Heart, Sparkles, Camera, Mic, Volume2 } from 'lucide-react';

export const HomeworkRecording: React.FC = () => {
  const [selectedActivity, setSelectedActivity] = useState<'drawing' | 'recording' | 'story'>('drawing');
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [storyText, setStoryText] = useState('');
  const [showFrench, setShowFrench] = useState(false);

  const drawingPrompts = [
    {
      english: "Draw Betty opening the magical blue door",
      french: "Dessine Betty ouvrant la porte magique bleue",
      colors: ["blue", "brown", "green"],
      emoji: "🚪✨"
    },
    {
      english: "Draw Luna the white unicorn with her pink horn",
      french: "Dessine Luna la licorne blanche avec sa corne rose",
      colors: ["white", "pink", "silver"],
      emoji: "🦄💖"
    },
    {
      english: "Draw Spark the green dragon with orange wings",
      french: "Dessine Spark le dragon vert avec des ailes oranges",
      colors: ["green", "orange", "red"],
      emoji: "🐉🧡"
    },
    {
      english: "Draw five purple butterflies dancing in the garden",
      french: "Dessine cinq papillons violets qui dansent dans le jardin",
      colors: ["purple", "yellow", "pink"],
      emoji: "🦋💜"
    },
    {
      english: "Draw Betty, Luna, and Spark as best friends",
      french: "Dessine Betty, Luna et Spark comme meilleurs amis",
      colors: ["rainbow", "happy", "friendship"],
      emoji: "👫🌈"
    }
  ];

  const speakingPrompts = [
    {
      english: "Tell me about your favorite color. What things are this color?",
      french: "Parle-moi de ta couleur préférée. Quelles choses sont de cette couleur ?",
      vocabulary: ["red", "blue", "yellow", "green", "purple", "pink", "orange", "white"]
    },
    {
      english: "Count from 1 to 5 and tell me what you can count in your room",
      french: "Compte de 1 à 5 et dis-moi ce que tu peux compter dans ta chambre",
      vocabulary: ["one", "two", "three", "four", "five"]
    },
    {
      english: "Which magical friend do you like best: Luna or Spark? Why?",
      french: "Quel ami magique préfères-tu : Luna ou Spark ? Pourquoi ?",
      vocabulary: ["unicorn", "dragon", "friendly", "magical", "beautiful"]
    }
  ];

  const storyPrompts = [
    {
      english: "Write about your own magical adventure. What magical door would you find?",
      french: "Écris ta propre aventure magique. Quelle porte magique trouverais-tu ?",
      starter: "One day, I found a magical door. It was..."
    },
    {
      english: "Create a new friend for Betty. What magical creature would you add to the story?",
      french: "Crée un nouvel ami pour Betty. Quelle créature magique ajouterais-tu à l'histoire ?",
      starter: "Betty meets a new magical friend. This friend is..."
    }
  ];

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/wav' });
        setAudioBlob(blob);
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Please allow microphone access to record your voice!');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  const playRecording = () => {
    if (audioBlob) {
      const audio = new Audio(URL.createObjectURL(audioBlob));
      audio.play();
    }
  };

  const downloadRecording = () => {
    if (audioBlob) {
      const url = URL.createObjectURL(audioBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'betty-homework-recording.wav';
      a.click();
    }
  };

  const playAudio = (text: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = showFrench ? 'fr-FR' : 'en-US';
      utterance.rate = 0.7;
      utterance.pitch = 1.1;
      speechSynthesis.speak(utterance);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <CardHeader>
          <CardTitle className="text-2xl text-center text-purple-600 flex items-center justify-center gap-2">
            <Sparkles className="w-6 h-6" />
            Betty's Magic Homework
            <Sparkles className="w-6 h-6" />
          </CardTitle>
          <p className="text-center text-gray-600">Choose your magical homework activity!</p>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <Button
              variant={selectedActivity === 'drawing' ? "default" : "outline"}
              onClick={() => setSelectedActivity('drawing')}
              className="h-20 flex flex-col items-center gap-2 bg-yellow-50 hover:bg-yellow-100 border-yellow-300"
            >
              <Palette className="w-6 h-6" />
              <span>🎨 Drawing Fun</span>
            </Button>
            <Button
              variant={selectedActivity === 'recording' ? "default" : "outline"}
              onClick={() => setSelectedActivity('recording')}
              className="h-20 flex flex-col items-center gap-2 bg-green-50 hover:bg-green-100 border-green-300"
            >
              <Mic className="w-6 h-6" />
              <span>🎤 Speaking Practice</span>
            </Button>
            <Button
              variant={selectedActivity === 'story' ? "default" : "outline"}
              onClick={() => setSelectedActivity('story')}
              className="h-20 flex flex-col items-center gap-2 bg-blue-50 hover:bg-blue-100 border-blue-300"
            >
              <Star className="w-6 h-6" />
              <span>📝 Story Writing</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Language Toggle */}
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => setShowFrench(!showFrench)}
              className="bg-purple-50 hover:bg-purple-100 border-purple-300"
            >
              {showFrench ? '🇬🇧 Show English' : '🇫🇷 Montrer Français'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Drawing Activity */}
      {selectedActivity === 'drawing' && (
        <Card className="bg-gradient-to-br from-yellow-50 to-orange-50">
          <CardHeader>
            <CardTitle className="text-xl text-orange-600 flex items-center gap-2">
              <Palette className="w-5 h-5" />
              🎨 Magic Drawing Activities
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-yellow-100 p-4 rounded-lg border-2 border-yellow-300">
              <h4 className="font-semibold text-yellow-800 mb-2">📝 Instructions:</h4>
              <p className="text-yellow-700 text-sm">
                {showFrench 
                  ? "Choisis une activité de dessin ci-dessous. Utilise les couleurs mentionnées et dessine avec soin. Montre ton dessin à tes parents ou ton professeur !"
                  : "Choose a drawing activity below. Use the colors mentioned and draw carefully. Show your drawing to your parents or teacher!"
                }
              </p>
            </div>

            <div className="grid md:grid-cols-1 gap-4">
              {drawingPrompts.map((prompt, index) => (
                <Card key={index} className="border-2 border-yellow-200 hover:border-yellow-400 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="text-3xl">{prompt.emoji}</div>
                      <div className="flex-grow">
                        <h4 className="font-semibold text-lg mb-2">
                          Activity {index + 1}
                        </h4>
                        <p className="text-gray-700 mb-3">
                          {showFrench ? prompt.french : prompt.english}
                        </p>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-sm font-medium text-gray-600">Colors to use:</span>
                          {prompt.colors.map((color, colorIndex) => (
                            <Badge key={colorIndex} className="bg-gray-100 text-gray-700 text-xs">
                              {color}
                            </Badge>
                          ))}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => playAudio(showFrench ? prompt.french : prompt.english)}
                          className="bg-white/80"
                        >
                          <Volume2 className="w-4 h-4 mr-1" />
                          Listen
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
              <h4 className="font-semibold text-green-700 mb-2">🌟 Drawing Tips:</h4>
              <ul className="text-green-600 text-sm space-y-1">
                <li>• Use bright, happy colors</li>
                <li>• Make your characters smile</li>
                <li>• Add sparkles and magic details</li>
                <li>• Write the English words next to your drawings</li>
                <li>• Have fun and be creative!</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Speaking Activity */}
      {selectedActivity === 'recording' && (
        <Card className="bg-gradient-to-br from-green-50 to-blue-50">
          <CardHeader>
            <CardTitle className="text-xl text-green-600 flex items-center gap-2">
              <Mic className="w-5 h-5" />
              🎤 Magic Speaking Practice
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-green-100 p-4 rounded-lg border-2 border-green-300">
              <h4 className="font-semibold text-green-800 mb-2">📝 Instructions:</h4>
              <p className="text-green-700 text-sm">
                {showFrench 
                  ? "Choisis une question ci-dessous. Clique sur 'Commencer l'enregistrement' et réponds à la question en anglais. Parle clairement et amusez-vous !"
                  : "Choose a question below. Click 'Start Recording' and answer the question in English. Speak clearly and have fun!"
                }
              </p>
            </div>

            <div className="space-y-4">
              {speakingPrompts.map((prompt, index) => (
                <Card key={index} className="border-2 border-green-200">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-lg mb-2">
                      Question {index + 1}
                    </h4>
                    <p className="text-gray-700 mb-3">
                      {showFrench ? prompt.french : prompt.english}
                    </p>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm font-medium text-gray-600">Helpful words:</span>
                      {prompt.vocabulary.map((word, wordIndex) => (
                        <Badge 
                          key={wordIndex} 
                          className="bg-blue-100 text-blue-700 text-xs cursor-pointer hover:bg-blue-200"
                          onClick={() => playAudio(word)}
                        >
                          {word}
                        </Badge>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => playAudio(showFrench ? prompt.french : prompt.english)}
                      className="bg-white/80"
                    >
                      <Volume2 className="w-4 h-4 mr-1" />
                      Listen to Question
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Recording Controls */}
            <Card className="border-2 border-blue-200">
              <CardContent className="p-6 text-center">
                <h4 className="font-semibold text-lg mb-4">🎙️ Record Your Answer</h4>
                
                <div className="space-y-4">
                  {!isRecording && !audioBlob && (
                    <Button
                      onClick={startRecording}
                      className="bg-red-500 hover:bg-red-600 text-white px-8 py-3"
                    >
                      <Mic className="w-5 h-5 mr-2" />
                      Start Recording
                    </Button>
                  )}

                  {isRecording && (
                    <div className="space-y-3">
                      <div className="text-red-600 font-semibold flex items-center justify-center gap-2">
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                        Recording... Speak now!
                      </div>
                      <Button
                        onClick={stopRecording}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-8 py-3"
                      >
                        Stop Recording
                      </Button>
                    </div>
                  )}

                  {audioBlob && (
                    <div className="space-y-3">
                      <div className="text-green-600 font-semibold">
                        ✅ Recording Complete!
                      </div>
                      <div className="flex gap-3 justify-center">
                        <Button
                          onClick={playRecording}
                          variant="outline"
                          className="bg-blue-50 hover:bg-blue-100"
                        >
                          <Volume2 className="w-4 h-4 mr-1" />
                          Play Recording
                        </Button>
                        <Button
                          onClick={downloadRecording}
                          variant="outline"
                          className="bg-green-50 hover:bg-green-100"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                        <Button
                          onClick={() => {
                            setAudioBlob(null);
                            setIsRecording(false);
                          }}
                          variant="outline"
                          className="bg-yellow-50 hover:bg-yellow-100"
                        >
                          Record Again
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      )}

      {/* Story Writing Activity */}
      {selectedActivity === 'story' && (
        <Card className="bg-gradient-to-br from-blue-50 to-purple-50">
          <CardHeader>
            <CardTitle className="text-xl text-blue-600 flex items-center gap-2">
              <Star className="w-5 h-5" />
              📝 Magic Story Writing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-blue-100 p-4 rounded-lg border-2 border-blue-300">
              <h4 className="font-semibold text-blue-800 mb-2">📝 Instructions:</h4>
              <p className="text-blue-700 text-sm">
                {showFrench 
                  ? "Choisis une idée d'histoire ci-dessous. Écris 3-5 phrases simples en anglais. Utilise les mots que tu as appris dans l'histoire de Betty !"
                  : "Choose a story idea below. Write 3-5 simple sentences in English. Use the words you learned from Betty's story!"
                }
              </p>
            </div>

            <div className="space-y-4">
              {storyPrompts.map((prompt, index) => (
                <Card key={index} className="border-2 border-blue-200">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-lg mb-2">
                      Story Idea {index + 1}
                    </h4>
                    <p className="text-gray-700 mb-3">
                      {showFrench ? prompt.french : prompt.english}
                    </p>
                    <div className="bg-gray-50 p-3 rounded border">
                      <p className="text-sm text-gray-600 mb-1">Story starter:</p>
                      <p className="italic text-gray-700">"{prompt.starter}"</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => playAudio(showFrench ? prompt.french : prompt.english)}
                      className="bg-white/80 mt-3"
                    >
                      <Volume2 className="w-4 h-4 mr-1" />
                      Listen
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Writing Area */}
            <Card className="border-2 border-purple-200">
              <CardContent className="p-6">
                <h4 className="font-semibold text-lg mb-4">✍️ Write Your Story Here</h4>
                <Textarea
                  value={storyText}
                  onChange={(e) => setStoryText(e.target.value)}
                  placeholder="Start writing your magical story here... Remember to use simple sentences and the vocabulary from Betty's adventure!"
                  className="min-h-32 text-lg"
                />
                <div className="mt-4 text-center">
                  <Badge className="bg-purple-100 text-purple-700">
                    Words written: {storyText.split(' ').filter(word => word.length > 0).length}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
              <h4 className="font-semibold text-purple-700 mb-2">🌟 Writing Tips:</h4>
              <ul className="text-purple-600 text-sm space-y-1">
                <li>• Use colors: blue, red, green, yellow, purple, pink, orange, white</li>
                <li>• Use numbers: one, two, three, four, five</li>
                <li>• Use describing words: happy, sad, magical, beautiful, friendly</li>
                <li>• Use action words: open, find, dance, help, work</li>
                <li>• Keep sentences simple and clear</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submission Instructions */}
      <Card className="bg-gradient-to-r from-pink-50 to-red-50 border-2 border-pink-200">
        <CardHeader>
          <CardTitle className="text-xl text-pink-600 flex items-center gap-2">
            <Heart className="w-5 h-5" />
            📧 Share Your Magic Work
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-gray-700">
              {showFrench 
                ? "Quand tu as terminé ton travail magique, partage-le avec ton professeur !"
                : "When you finish your magic work, share it with your teacher!"
              }
            </p>
            
            <div className="bg-white p-4 rounded-lg border border-pink-200">
              <h4 className="font-semibold text-pink-700 mb-2">📱 How to Share:</h4>
              <ul className="text-pink-600 text-sm space-y-2">
                <li className="flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  <span><strong>Drawings:</strong> Take a photo and email to your teacher</span>
                </li>
                <li className="flex items-center gap-2">
                  <Mic className="w-4 h-4" />
                  <span><strong>Recordings:</strong> Download the audio file and send it</span>
                </li>
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  <span><strong>Stories:</strong> Copy your text and email it</span>
                </li>
              </ul>
            </div>

            <div className="text-center">
              <Badge className="bg-pink-100 text-pink-700 text-sm px-4 py-2">
                📧 Teacher Email: mariannebeasley@enterprisingenglish.fr
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};